import { Button, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { useState } from "react";
import type { z } from "zod";
import {
  CareplanAvailableAction,
  useCarePlanSummary,
} from "../network/useCarePlanSummary";
import { useCreateDisposition } from "../network/useCreateDisposition";
import { DispositionEditor } from "./DispositionEditor";
import type { editDispositionSchema } from "./dispositionSchemas";

interface AddDispositionProps {
  carePlanId: string;
  encounterId: string;
}

export const AddDisposition = ({
  carePlanId,
  encounterId,
}: AddDispositionProps) => {
  const [isAddingDisposition, setIsAddingDisposition] = useState(false);

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canAddDisposition = isActionAvailable(
    CareplanAvailableAction.ADD_DISPOSITION,
    careplanAvailableActions,
  );

  const createDisposition = useCreateDisposition({ encounterId, carePlanId });

  const handleClick = () => {
    trackEvent(TRACKERS.ADD_DISPOSITION);
    setIsAddingDisposition(true);
  };

  const onSubmit = async (values: z.infer<typeof editDispositionSchema>) => {
    try {
      await createDisposition.mutateAsync({
        dispositionCode: values.code,
        dispositionDescription: values.text,
      });

      setIsAddingDisposition(false);
    } catch (error) {
      toast.error("Failed to add disposition.");
    }
  };

  if (isAddingDisposition)
    return (
      <div className="border rounded-md px-3 py-2">
        <DispositionEditor
          onSubmit={onSubmit}
          onCancel={() => setIsAddingDisposition(false)}
          isLoading={createDisposition.isPending}
        />
      </div>
    );

  return (
    <Button
      variant="secondary"
      size="xs"
      onClick={handleClick}
      className="w-fit mt-2"
      disabled={!canAddDisposition}
    >
      Add Disposition
    </Button>
  );
};
